import { motion } from 'framer-motion';
import type { MenuItem } from '@happyspace/sdk/restaurant';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import {
  UtensilsCrossed,
  Drumstick,
  Beef,
  Sandwich,
  Soup,
  CupSoda,
  Plus,
  Minus,
} from 'lucide-react';
import { stripHtml, money } from '../lib/site';
import { useCartUI } from './Layout';

export function categoryIcon(name: string) {
  const n = (name || '').toLowerCase();
  if (n.includes('shawarma') || n.includes('combo')) return Beef;
  if (n.includes('gyro') || n.includes('burger') || n.includes('sandwich')) return Sandwich;
  if (n.includes('platter')) return Soup;
  if (n.includes('drink')) return CupSoda;
  if (n.includes('wing') || n.includes('kid') || n.includes('side')) return Drumstick;
  return UtensilsCrossed;
}

const BLURBS: Record<string, string> = {
  'Lamb/Beef Shawarma': 'Hand-carved lamb and beef, slow-roasted and warmly spiced.',
  'Chicken Shawarma Combo': 'Marinated chicken shawarma served with fries and a drink.',
  'Kabob Sandwich': 'Char-grilled kabob wrapped fresh with veggies and sauce.',
  'Shish Kabob Combo': 'Skewered shish kabob, grilled to order, with fries and a drink.',
};

export function dishBlurb(item: { title: string; description?: string }) {
  return BLURBS[item.title] || stripHtml(item.description) || 'Made fresh to order.';
}

/**
 * Dish card used on the home preview and the full menu.
 *
 * No dish photography exists for this business yet (every menu item comes back
 * without an `image`), so cards fall back to a branded tile and pick up real
 * photos automatically once items carry one.
 */
export default function DishCard({ item, index = 0 }: { item: MenuItem; index?: number }) {
  const { items: cartItems, addItem, updateQuantity } = useRestaurantCart();
  const { openCart } = useCartUI();
  const Icon = categoryIcon(item.category);
  const soldOut = item.inStock === false || item.available === false;

  // Find this dish's line in the cart (plain add, no modifiers picked here) so
  // the card can show a live +/- stepper instead of a one-shot "Added" flash.
  const cartLine = cartItems.find(
    (line) => line._id === item._id && (!line.selectedModifiers || line.selectedModifiers.length === 0),
  );
  const quantity = cartLine?.quantity ?? 0;

  function handleAdd() {
    addItem(item, [], 1);
  }

  function handleIncrease() {
    if (cartLine) updateQuantity(cartLine.cartItemKey, quantity + 1);
    else addItem(item, [], 1);
  }

  function handleDecrease() {
    if (cartLine) updateQuantity(cartLine.cartItemKey, quantity - 1);
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, delay: (index % 5) * 0.06 }}
      className="group flex flex-col overflow-hidden rounded-lg bg-white border transition-shadow duration-300 hover:shadow-[var(--shadow-card)]"
      style={{ borderColor: 'var(--color-line)' }}
    >
      <div
        className="relative aspect-[4/3] overflow-hidden"
        style={{ background: 'var(--color-cream)' }}
      >
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <span
              className="w-16 h-16 rounded-full border-2 flex items-center justify-center"
              style={{ borderColor: 'var(--color-yellow)', background: '#fff' }}
            >
              <Icon size={26} style={{ color: 'var(--color-ink)' }} />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-2">
              {item.category}
            </span>
          </div>
        )}
        {soldOut && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/75 text-[11px] font-extrabold uppercase tracking-widest text-ink">
            Sold out
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-[15px] font-extrabold uppercase tracking-wide text-ink leading-snug">
          {item.title}
        </h3>
        <p className="mt-2 flex-1 text-[13px] text-ink-2 leading-relaxed line-clamp-3">
          {dishBlurb(item)}
        </p>
        <p className="mt-3 display text-xl" style={{ color: 'var(--color-ink)' }}>
          {money(item.basePrice)}
        </p>

        {/* Fixed-height actions block: the stepper state and the plain-button
            state always occupy the same total height (button/stepper row +
            a reserved "View cart" line, hidden but present when not needed)
            so cards never change height across a grid row when a quantity
            is added. */}
        <div className="mt-3 flex flex-col gap-1.5">
          {quantity > 0 ? (
            <div
              className="flex w-full items-center justify-between rounded-md !py-2.5 !px-3"
              style={{ background: 'var(--color-yellow)' }}
            >
              <button
                onClick={handleDecrease}
                aria-label={`Decrease ${item.title} quantity`}
                className="flex items-center justify-center text-ink hover:opacity-70 transition-opacity"
              >
                <Minus size={16} strokeWidth={2.5} />
              </button>
              <span className="text-sm font-extrabold text-ink" aria-live="polite">
                {quantity}
              </span>
              <button
                onClick={handleIncrease}
                aria-label={`Increase ${item.title} quantity`}
                className="flex items-center justify-center text-ink hover:opacity-70 transition-opacity"
              >
                <Plus size={16} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAdd}
              disabled={soldOut}
              aria-label={`Add ${item.title} to cart`}
              className="btn-yellow w-full !py-2.5 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} /> Add to Cart
            </button>
          )}
          <button
            onClick={openCart}
            tabIndex={quantity > 0 ? 0 : -1}
            aria-hidden={quantity === 0}
            className={`text-[11px] font-bold uppercase tracking-wide transition-colors ${
              quantity > 0 ? 'text-ink-2 hover:text-ink' : 'invisible pointer-events-none'
            }`}
          >
            View cart
          </button>
        </div>
      </div>
    </motion.article>
  );
}
